import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { useFamily } from './FamilyContext'
import {
  getChapterStatuses,
  getUnlockedPlaygrounds,
  loadProgress,
  completeChapter as completeChapterApi,
} from '@/services/storytellingService'
import {
  loadSecrets,
  unlockSecret as unlockSecretApi,
  type SecretWithMeta,
} from '@/services/storySecretService'
import type { ChapterStatus, StorySecret } from '@/types/storytelling'

interface ChapterStatusEntry {
  chapterId: string
  status: ChapterStatus
}

interface StorytellingContextValue {
  /** Chapter statuses for the course map */
  chapterStatuses: ChapterStatusEntry[]
  /** IDs of playgrounds that are unlocked */
  unlockedPlaygrounds: string[]
  /** Unlocked story secrets */
  secrets: SecretWithMeta[]
  /** Complete a chapter and unlock the next */
  completeChapter: (chapterId: string) => Promise<boolean>
  /** Unlock a story secret */
  unlockSecret: (secret: StorySecret, chapterId: string) => Promise<boolean>
  /** Whether data is still loading */
  isLoading: boolean
  /** Refresh data from Supabase */
  refresh: () => Promise<void>
}

const StorytellingContext = createContext<StorytellingContextValue | undefined>(undefined)

export function StorytellingProvider({ children }: { children: ReactNode }) {
  const { activeWriter } = useFamily()
  const [progressRows, setProgressRows] = useState<
    { child_id: string; chapter_id: string; status: ChapterStatus; completed_at: string | null }[]
  >([])
  const [secrets, setSecrets] = useState<SecretWithMeta[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const loadData = useCallback(async () => {
    if (!activeWriter) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    const [progressData, secretsData] = await Promise.all([
      loadProgress(activeWriter.id),
      loadSecrets(activeWriter.id),
    ])

    setProgressRows(progressData)
    setSecrets(secretsData)
    setIsLoading(false)
  }, [activeWriter])

  useEffect(() => {
    loadData()
  }, [loadData])

  const chapterStatuses = useMemo(
    () => getChapterStatuses(progressRows),
    [progressRows]
  )

  const unlockedPlaygrounds = useMemo(() => {
    const completedIds = progressRows
      .filter((p) => p.status === 'completed')
      .map((p) => p.chapter_id)
    return getUnlockedPlaygrounds(completedIds)
  }, [progressRows])

  const completeChapter = useCallback(
    async (chapterId: string): Promise<boolean> => {
      if (!activeWriter) return false
      const success = await completeChapterApi(activeWriter.id, chapterId)
      if (success) await loadData()
      return success
    },
    [activeWriter, loadData]
  )

  const unlockSecret = useCallback(
    async (secret: StorySecret, chapterId: string): Promise<boolean> => {
      if (!activeWriter) return false
      const success = await unlockSecretApi(activeWriter.id, secret, chapterId)
      if (success) await loadData()
      return success
    },
    [activeWriter, loadData]
  )

  return (
    <StorytellingContext.Provider
      value={{
        chapterStatuses,
        unlockedPlaygrounds,
        secrets,
        completeChapter,
        unlockSecret,
        isLoading,
        refresh: loadData,
      }}
    >
      {children}
    </StorytellingContext.Provider>
  )
}

export function useStorytelling() {
  const context = useContext(StorytellingContext)
  if (context === undefined) {
    throw new Error('useStorytelling must be used within a StorytellingProvider')
  }
  return context
}
